import React, { useEffect, useMemo, useState } from 'react';
import {
  ADMIN_DOMAINS,
  listDomainRecords,
  createDomainRecord,
  updateDomainRecord,
  deleteDomainRecord,
  getAdminDomainLookups,
} from '../../services/adminApi.service';
import { showError, showSuccess } from '../../services/notification.service';
import './AdminPortal.css';

const toStringValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const parseBoolean = (raw) => raw === true || raw === 'true' || raw === 1 || raw === '1';

const parseFieldValue = (raw, field) => {
  if (field.type === 'number') return raw === '' ? null : Number(raw);
  if (field.type === 'boolean') return parseBoolean(raw);
  if (field.type === 'json') {
    if (!raw || String(raw).trim() === '') return null;
    return JSON.parse(raw);
  }
  if (field.type === 'components_table') {
    if (Array.isArray(raw)) return raw;
    if (!raw) return [];
    return JSON.parse(raw);
  }
  if (field.type === 'select') {
    if (raw === '' || raw === undefined || raw === null) return null;
    if (field.options?.some((o) => typeof o.value === 'number')) return Number(raw);
    return raw;
  }
  return raw;
};

const isBlank = (v) => v === undefined || v === null || String(v).trim() === '';

const normalizeComponents = (value) => {
  if (!value) return [];
  const rows = Array.isArray(value) ? value : [];
  return rows
    .map((r) => ({
      PermissionId: String(r?.PermissionId ?? '').trim(),
      ComponentId: String(r?.ComponentId ?? '').trim(),
    }))
    .filter((r) => r.PermissionId || r.ComponentId);
};

function AdminCrudPanel({ domain }) {
  const config = ADMIN_DOMAINS[domain];
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [lookups, setLookups] = useState({});
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(100);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [formMode, setFormMode] = useState(''); // create | edit | ''
  const [formValues, setFormValues] = useState({});
  const [activeRecord, setActiveRecord] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  const fieldDefs = useMemo(() => config.formFields || [], [config]);

  const headerColumns = useMemo(() => {
    if (Array.isArray(config.tableColumns) && config.tableColumns.length > 0) {
      return config.tableColumns;
    }
    if (!records.length) return [];
    const available = new Set(records.flatMap((r) => Object.keys(r || {})));
    return [config.idField, 'Name', 'Description', 'Status'].filter((c) => available.has(c));
  }, [config, records]);

  const visibleRecords = useMemo(() => {
    let rows = records;

    if (statusFilter !== '') {
      rows = rows.filter((row) => String(row?.Status ?? row?.assocStatus ?? '') === statusFilter);
    }

    if (!search.trim()) return rows;
    const needle = search.toLowerCase();
    return rows.filter((row) =>
      headerColumns.some((col) => {
        const value = row?.[col];
        if (Array.isArray(value) || (value && typeof value === 'object')) {
          return JSON.stringify(value).toLowerCase().includes(needle);
        }
        return String(value ?? '').toLowerCase().includes(needle);
      })
    );
  }, [records, search, headerColumns, statusFilter]);

  const setFormDefaults = (source = {}) => {
    const next = {};
    fieldDefs.forEach((f) => {
      const sourceVal = source[f.name];
      if (f.type === 'components_table') {
        next[f.name] = normalizeComponents(sourceVal || f.defaultValue || []);
        return;
      }

      if (sourceVal !== undefined && sourceVal !== null) {
        next[f.name] = toStringValue(sourceVal);
      } else if (f.defaultValue !== undefined) {
        next[f.name] = typeof f.defaultValue === 'object'
          ? JSON.stringify(f.defaultValue, null, 2)
          : String(f.defaultValue);
      } else if (f.type === 'boolean') {
        next[f.name] = false;
      } else {
        next[f.name] = '';
      }
    });
    return next;
  };

  const reload = async () => {
    setLoading(true);
    try {
      const result = await listDomainRecords(domain, { skip, take });
      setRecords(result.records || []);
    } catch (e) {
      showError(e?.response?.data?.error || e?.message || `Failed to fetch ${config.label}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, skip, take]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const data = await getAdminDomainLookups();
        setLookups(data || {});
      } catch {
        // Silent fallback: text entry still works.
      }
    };
    loadLookups();
  }, [domain]);

  const buildPayload = () => {
    const payload = {};
    try {
      fieldDefs.forEach((f) => {
        const rawVal = formValues[f.name];
        if (rawVal === '' || rawVal === undefined || rawVal === null) return;
        payload[f.name] = parseFieldValue(rawVal, f);
      });

      if (domain === 'accounts') {
        if (isBlank(payload.Name) && !isBlank(payload.AccountName)) {
          payload.Name = payload.AccountName;
        }
        if (isBlank(payload.AccountName) && !isBlank(payload.Name)) {
          payload.AccountName = payload.Name;
        }
      }

      for (const f of fieldDefs) {
        if (!f.required) continue;
        const val = payload[f.name];
        if (val === undefined || val === null || (typeof val === 'string' && val.trim() === '')) {
          throw new Error(`${f.label} is required`);
        }
      }

      return payload;
    } catch (e) {
      throw new Error(e?.message || 'Invalid value in form. Please check form fields.');
    }
  };

  const openCreate = () => {
    setActiveRecord(null);
    setFormValues(setFormDefaults());
    setFormMode('create');
  };

  const openEdit = (record) => {
    setActiveRecord(record);
    setFormValues(setFormDefaults(record));
    setFormMode('edit');
  };

  const closeForm = () => {
    setFormMode('');
    setActiveRecord(null);
    setFormValues({});
  };

  const handleSaveForm = async () => {
    try {
      const payload = buildPayload();
      if (formMode === 'create') {
        await createDomainRecord(domain, payload);
        showSuccess(`${config.label} record created`);
      } else if (formMode === 'edit') {
        const recordId = activeRecord?.[config.idField] || activeRecord?.UserId;
        if (!recordId && domain !== 'userAssociations') {
          throw new Error(`${config.idField} missing in selected row`);
        }

        const baseRecord = { ...(activeRecord || {}) };
        delete baseRecord.CreatedDate;
        delete baseRecord.ModifiedDate;

        const finalPayload = {
          ...baseRecord,
          ...payload,
          ...(activeRecord?.etag ? { etag: activeRecord.etag } : {}),
        };

        await updateDomainRecord(domain, recordId, finalPayload, '', activeRecord);
        showSuccess(`${config.label} record updated`);
      }
      closeForm();
      reload();
    } catch (e) {
      showError(e?.response?.data?.error || e?.message || 'Save failed');
    }
  };

  const requestDelete = (record) => {
    setRecordToDelete(record);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const recordId = recordToDelete?.[config.idField] || recordToDelete?.UserId;
      if (!recordId && domain !== 'userAssociations') {
        throw new Error(`${config.idField} missing in selected row`);
      }
      await deleteDomainRecord(domain, recordId, '', recordToDelete);
      showSuccess(`${config.label} record deleted`);
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
      reload();
    } catch (e) {
      showError(e?.response?.data?.error || e?.message || 'Delete failed');
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setRecordToDelete(null);
  };

  const updateComponents = (fieldName, index, patch) => {
    setFormValues((prev) => {
      const current = normalizeComponents(prev[fieldName]);
      const next = [...current];
      next[index] = { ...next[index], ...patch };
      return { ...prev, [fieldName]: next };
    });
  };

  const addComponentRow = (fieldName) => {
    setFormValues((prev) => {
      const current = normalizeComponents(prev[fieldName]);
      return {
        ...prev,
        [fieldName]: [...current, { PermissionId: '3', ComponentId: '' }],
      };
    });
  };

  const removeComponentRow = (fieldName, index) => {
    setFormValues((prev) => {
      const current = normalizeComponents(prev[fieldName]);
      const next = current.filter((_, idx) => idx !== index);
      return { ...prev, [fieldName]: next };
    });
  };

  const renderCell = (value) => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) {
      if (!value.length) return '[]';
      if (typeof value[0] === 'object') {
        return value.map((item) => {
          const componentId = item?.ComponentId || '';
          const permissionId = item?.PermissionId || '';
          return `${componentId}${permissionId ? ` (P:${permissionId})` : ''}`;
        }).join(', ');
      }
      return value.join(', ');
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  return (
    <div className="admin-crud-panel">
      <div className="admin-crud-header">
        <h3>{config.label}</h3>
        <div className="admin-crud-controls">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search rows..."
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
          <input
            type="number"
            min="0"
            value={skip}
            onChange={(e) => setSkip(Number(e.target.value || 0))}
            placeholder="skip"
          />
          <input
            type="number"
            min="1"
            max="500"
            value={take}
            onChange={(e) => setTake(Math.min(500, Math.max(1, Number(e.target.value || 1))))}
            placeholder="take"
          />
          <button className="btn-admin-primary" onClick={openCreate}>
            + New
          </button>
          <button className="btn-admin-primary" onClick={reload} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              {headerColumns.map((col) => <th key={col}>{col}</th>)}
              {!headerColumns.length && <th>Records</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!visibleRecords.length ? (
              <tr>
                <td colSpan={Math.max(2, headerColumns.length + 1)} className="admin-empty">No records found</td>
              </tr>
            ) : (
              visibleRecords.map((row, index) => (
                <tr key={row?.[config.idField] || `${row?.UserId || 'row'}-${index}`}>
                  {headerColumns.length ? headerColumns.map((col) => (
                    <td key={col}>{renderCell(row?.[col])}</td>
                  )) : (
                    <td>{toStringValue(row)}</td>
                  )}
                  <td className="admin-actions-cell">
                    <button className="btn-admin-secondary" onClick={() => openEdit(row)}>Edit</button>
                    <button className="btn-admin-danger" onClick={() => requestDelete(row)}>Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {formMode && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal-box admin-form-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{formMode === 'create' ? `Create ${config.label}` : `Update ${config.label}`}</h3>
            <div className="admin-form-grid">
              {fieldDefs.map((field) => {
                const selectOptions = field.options || (field.source ? (lookups[field.source] || []) : []);

                return (
                  <div className="modal-field" key={field.name}>
                    <label>
                      {field.label} {field.required ? '*' : ''}
                    </label>
                    {field.type === 'boolean' ? (
                      <select
                        value={String(formValues[field.name])}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, [field.name]: e.target.value === 'true' }))}
                      >
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </select>
                    ) : field.type === 'json' ? (
                      <textarea
                        rows={4}
                        value={formValues[field.name] || ''}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                      />
                    ) : field.type === 'components_table' ? (
                      <div className="components-table-editor">
                        <table className="components-inline-table">
                          <thead>
                            <tr>
                              <th>PermissionId</th>
                              <th>ComponentId</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {normalizeComponents(formValues[field.name]).map((row, idx) => (
                              <tr key={`${field.name}-${idx}`}>
                                <td>
                                  <input
                                    type="text"
                                    value={row.PermissionId || ''}
                                    onChange={(e) => updateComponents(field.name, idx, { PermissionId: e.target.value })}
                                  />
                                </td>
                                <td>
                                  <select
                                    value={row.ComponentId || ''}
                                    onChange={(e) => updateComponents(field.name, idx, { ComponentId: e.target.value })}
                                  >
                                    <option value="">Select component</option>
                                    {(lookups[field.componentSource] || []).map((opt) => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn-admin-danger"
                                    onClick={() => removeComponentRow(field.name, idx)}
                                  >
                                    -
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <button
                          type="button"
                          className="btn-admin-secondary"
                          onClick={() => addComponentRow(field.name)}
                        >
                          + Add Component
                        </button>
                      </div>
                    ) : field.type === 'select' ? (
                      <select
                        value={formValues[field.name] || ''}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                      >
                        <option value="">Select...</option>
                        {selectOptions.map((opt) => (
                          <option key={String(opt.value)} value={String(opt.value)}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type === 'number' ? 'number' : 'text'}
                        value={formValues[field.name] || ''}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                        required={field.required}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={closeForm}>Cancel</button>
              <button className="btn-save" onClick={handleSaveForm}>
                {formMode === 'create' ? 'Create' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Confirmation</h3>
            <p className="modal-email">
              Are you sure you want to delete this {config.label} record?
            </p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={cancelDelete}>Cancel</button>
              <button className="btn-save btn-admin-danger" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCrudPanel;
